import { storageImage, storageSrcSet } from "@/lib/imageUrl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, Mail, Phone, Sparkles, Video, TrendingUp, Users, Zap, Camera, Clock, Lightbulb, Target, BarChart3, Heart, ChevronDown, ArrowRight, Share2 } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, AnimatedCollapsibleContent } from "@/components/ui/collapsible";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SocialMediaServicesGrid } from "@/components/SocialMediaServicesGrid";
import { BookingDialog } from "@/components/BookingDialogLazy";
import { SEOHead } from "@/components/SEOHead";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useAdvancedScrollAnimation } from "@/hooks/useAdvancedScrollAnimation";
import { ParallaxLayer } from "@/components/animations/ParallaxSection";
import { Card3D } from "@/components/animations/Card3D";
import { MagneticButton } from "@/components/animations/MagneticButton";
import { WordByWordReveal } from "@/components/animations/WordByWordReveal";
import { AnimatedGradientText } from "@/components/animations/AnimatedGradientText";
import { FloatingParticles } from "@/components/animations/FloatingParticles";
import { ScrollIndicator } from "@/components/animations/ScrollIndicator";
import { servicesData } from "@/data/servicesData";
import { trackWhatsAppClick, trackPhoneClick, trackEmailClick } from "@/lib/analytics";
import { useProjects } from "@/hooks/useProjects";
import { LazyCoverImage } from "@/components/LazyCoverImage";
import { Skeleton } from "@/components/ui/skeleton";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useHeroImage } from "@/hooks/useHeroImages";
import { RegionalFAQSection } from "@/components/seo/RegionalFAQSection";
import PlatformStrategies from "@/components/PlatformStrategies";
import { MarketFilter } from "@/components/MarketFilter";
import ContentCalendarExample from "@/components/ContentCalendarExample";
import SuccessMetrics from "@/components/SuccessMetrics";
import SocialMediaPackages from "@/components/SocialMediaPackages";
import { Badge } from "@/components/ui/badge";
import GoogleRating from "@/components/GoogleRating";
import { BreadcrumbSchema, ServiceSchema } from "@/components/StructuredData";

const SocialMedia = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [openSolutions, setOpenSolutions] = useState<number[]>([]);
  const [openWhyUs, setOpenWhyUs] = useState<number[]>([]);
  const heroAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', duration: 1000 });
  const problemAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', threshold: 0.15 });
  const impactAnim = useAdvancedScrollAnimation({ variant: 'fadeLeft', threshold: 0.15 });
  const solutionsAnim = useAdvancedScrollAnimation({ variant: 'scale', threshold: 0.1 });
  const whyUsAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', threshold: 0.1 });
  const servicesAnim = useAdvancedScrollAnimation({ variant: 'blur', threshold: 0.1 });
  const portfolioAnim = useAdvancedScrollAnimation({ variant: 'fadeUp', threshold: 0.1 });
  const ctaAnim = useAdvancedScrollAnimation({ variant: 'scale', threshold: 0.15 });
  
  const { data: projects = [], isLoading: projectsLoading } = useProjects({ publicOnly: true });
  const { data: heroImageData } = useHeroImage("socialmedia");
  const [selectedMarket, setSelectedMarket] = useState<'de' | 'en'>('de');
  const socialMediaProjects = projects.filter(p => p.category === "social-media" && p.language === selectedMarket);
  const heroImageUrl = heroImageData?.image_url;


  const toggleSolution = (index: number) => {
    setOpenSolutions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleWhyUs = (index: number) => {
    setOpenWhyUs(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const whatsappHref = `https://wa.me/4917670027200?text=${encodeURIComponent("Hallo Clicks DMP, ich interessiere mich für Social Media Services!")}`;
  const phoneHref = "tel:+4917670027200";
  const emailHref = "mailto:info@clicksdmp.com?subject=Social%20Media%20Anfrage";

  const handleWhatsApp = () => {
    trackWhatsAppClick('social_media_page');
  };

  const handlePhone = () => {
    trackPhoneClick();
  };

  const handleEmail = () => {
    trackEmailClick();
  };

  return (
    <>
      <SEOHead
        title="Social Media Marketing & Content Creation | Clicks DMP"
        description="Professionelle Social Media Strategien, Content Creation und Community Management. Von Reels bis Stories – wir machen Ihre Marke sichtbar und erfolgreich."
        canonical="https://clicksdmp.com/socialmedia"
        ogImage="https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/gallery/1760101597818-hep3jl22i9i.jpg"
      />
      
      <BreadcrumbSchema items={[{ name: "Startseite", url: "https://clicksdmp.com/" }, { name: "Social Media", url: "https://clicksdmp.com/socialmedia" }]} />
      <ServiceSchema serviceType="Social-Media-Content" name="Social-Media-Content aus Mannheim" path="/socialmedia" description="Social-Media-Strategie, Content-Produktion und Community-Management: Reels, Stories und Fotos, produziert vor Ort und fertig zum Posten." />

      <div className="min-h-screen bg-background overflow-x-hidden">
        <Header />
        
        {/* Hero Section with Apple-Style Parallax */}
        <section 
          className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background"
          style={{
            animation: 'page-entry 1.5s ease-out forwards',
            opacity: 0
          }}
        >
          {heroImageUrl ? (
            <>
              {/* Layer 1: Background Image */}
              <ParallaxLayer speed={0.3} className="absolute inset-0">
                <img
                  src={storageImage(heroImageUrl, 1600, { quality: 78 })}
                  srcSet={storageSrcSet(heroImageUrl, [800, 1200, 1600], { quality: 78 })}
                  sizes="100vw"
                  fetchPriority="high"
                  alt="Social Media Hero"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </ParallaxLayer>

              {/* Layer 2: Gradient Overlay */}
              <ParallaxLayer speed={0.5} className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-background" />
              </ParallaxLayer>

              {/* Layer 3: Floating Particles */}
              <ParallaxLayer speed={0.7} className="absolute inset-0">
                <FloatingParticles count={6} />
              </ParallaxLayer>
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-primary/10" />
              <ParallaxLayer speed={0.5} className="absolute top-20 right-20 w-96 h-96">
                <div className="w-full h-full bg-primary/30 rounded-full blur-3xl animate-pulse" />
              </ParallaxLayer>
              <ParallaxLayer speed={-0.3} className="absolute bottom-20 left-20 w-96 h-96">
                <div className="w-full h-full bg-primary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
              </ParallaxLayer>
            </>
          )}
          
          {/* Layer 4: Text Content */}
          <ParallaxLayer speed={0.8} className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="container-custom text-center max-w-5xl">
              <div className="flex flex-col items-center gap-6">
                {/* Animated Headline */}
                <WordByWordReveal
                  text="Social Media Content für Unternehmen in Mannheim"
                  className={`text-4xl md:text-6xl lg:text-7xl font-bold ${heroImageUrl ? 'text-white' : ''}`}
                  startDelay={1000}
                  wordDelay={100}
                />
                <WordByWordReveal
                  as="p"
                  text="Social Media Marketing"
                  className={`text-4xl md:text-6xl lg:text-7xl font-bold ${heroImageUrl ? 'text-white' : ''}`}
                  startDelay={1400}
                  wordDelay={100}
                />
                
                {/* Animated Gradient Subtitle */}
                <AnimatedGradientText
                  className="text-2xl md:text-4xl lg:text-5xl font-bold"
                  delay={1800}
                >
                  Content der konvertiert
                </AnimatedGradientText>
                
                {/* Description */}
                <p 
                  className={`text-base md:text-lg max-w-3xl ${heroImageUrl ? 'text-gray-300' : 'text-muted-foreground'}`}
                  style={{
                    animation: 'fade-in 0.8s ease-out forwards',
                    animationDelay: '2.3s',
                    opacity: 0
                  }}
                >
                  Von Instagram Marketing bis Content Creation: 
                  Wir machen Unternehmen auf Social Media sichtbar und erfolgreich
                </p>

                {/* CTA Buttons */}
                <div 
                  className="flex flex-col sm:flex-row gap-4 mt-4 pointer-events-auto"
                  style={{
                    animation: 'fade-in 0.8s ease-out forwards',
                    animationDelay: '2.5s',
                    opacity: 0
                  }}
                >
                  <MagneticButton
                    onClick={() => setShowBooking(true)}
                    className="border-primary/30 bg-primary/5 text-primary backdrop-blur-xs hover:bg-primary/10 px-8 py-4 text-lg rounded-full font-semibold transition-all border flex items-center justify-center gap-2"
                    strength={0.3}
                  >
                    <Calendar className="w-5 h-5" />
                    Kostenlose Strategie-Session
                  </MagneticButton>
                  <MagneticButton
                    href={emailHref}
                    onClick={handleEmail}
                    className="border-primary/30 bg-primary/5 text-primary backdrop-blur-xs hover:bg-primary/10 px-8 py-4 text-lg rounded-full font-semibold transition-all border flex items-center justify-center gap-2"
                    strength={0.3}
                  >
                    <Mail className="w-5 h-5" />
                    E-Mail Kontakt
                  </MagneticButton>
                </div>
              </div>
            </div>
          </ParallaxLayer>

          {/* Scroll Indicator */}
          <ScrollIndicator targetId="regional-services" delay={2500} />
        </section>

        {/* Regional Service Areas Section */}
        <section id="regional-services" className="section-padding bg-gradient-to-b from-background to-primary/5">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Social Media Marketing in der <span className="text-gradient">Rhein-Neckar-Region</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-12">
                Mit Sitz in Mannheim sind wir Ihre professionelle Social Media Marketing Agentur für die gesamte Region – von Heidelberg über Ludwigshafen und Karlsruhe bis Stuttgart, Frankfurt, Mainz und Wiesbaden. Wir erstellen authentischen Content und entwickeln erfolgreiche Social Media Strategien direkt bei Ihnen vor Ort im Südwesten Deutschlands.
              </p>
            </div>
          </div>
        </section>

        {/* Problem Awareness Section */}
        <section id="problem" ref={problemAnim.ref} style={problemAnim.style} className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Kennen Sie das <span className="text-gradient">Gefühl?</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Mannheimer Unternehmen kämpfen oft mit diesen Herausforderungen
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Camera,
                  title: "Kamera-Scheu",
                  description: "\"Ich habe noch nie vor der Kamera gestanden und fühle mich unwohl dabei.\""
                },
                {
                  icon: Clock,
                  title: "Keine Zeit",
                  description: "\"Mein Tagesgeschäft lässt mir kaum Zeit für Social Media Content.\""
                },
                {
                  icon: Lightbulb,
                  title: "Ideenlos",
                  description: "\"Mir fehlen kreative Ideen, was ich überhaupt posten soll.\""
                },
                {
                  icon: TrendingUp,
                  title: "Keine Reichweite",
                  description: "\"Meine Posts erreichen einfach nicht die richtige Zielgruppe.\""
                }
              ].map((item, index) => (
                <Card3D
                  key={index}
                  className="relative group bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-8 hover:border-primary/40 transition-all duration-300"
                  intensity={10}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <item.icon className="relative w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="relative text-xl font-bold mb-3">{item.title}</h3>
                  <p className="relative text-muted-foreground italic">{item.description}</p>
                </Card3D>
              ))}
            </div>
          </div>
        </section>

        {/* Impact Section - How Content Makes a Difference */}
        <section id="impact" ref={impactAnim.ref} style={impactAnim.style} className="section-padding bg-gradient-to-b from-background to-primary/5">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Ein <span className="text-gradient glow-text">15-Sekunden Reel</span><br />
                  kann Ihr Business <span className="text-gradient">transformieren</span>
                </h2>
                <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                  Stellen Sie sich vor: Ein authentischer, lustiger Reel erreicht 50.000 Menschen. 
                  Davon werden 5.000 zu Ihrem Profil geleitet. 500 schauen sich Ihre Website an. 
                  50 nehmen Kontakt auf. 5 werden zu Kunden.
                </p>
                <div className="space-y-6">
                  {[
                    { icon: Heart, title: "Emotionale Verbindung", desc: "Authentische Inhalte schaffen Vertrauen und Nähe zu Ihrer Zielgruppe" },
                    { icon: Video, title: "Virale Reichweite", desc: "Der Instagram-Algorithmus liebt Reels – nutzen Sie diese Chance" },
                    { icon: Target, title: "Gezielte Ansprache", desc: "Erreichen Sie genau die Menschen, die Ihre Produkte brauchen" }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 items-start">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/10 rounded-3xl blur-3xl" />
                <div className="relative bg-gradient-to-br from-primary/10 to-background border border-primary/30 rounded-3xl p-8 space-y-6">
                  <div className="text-center p-6 bg-background/50 backdrop-blur-xs rounded-2xl border border-primary/20">
                    <div className="text-5xl font-bold text-primary mb-2">10x</div>
                    <p className="text-sm text-muted-foreground">Höhere Engagement-Rate mit Video-Content</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-background/50 backdrop-blur-xs rounded-xl border border-primary/20">
                      <div className="text-3xl font-bold text-primary mb-1">87%</div>
                      <p className="text-xs text-muted-foreground">Kaufentscheidungen durch Social Media</p>
                    </div>
                    <div className="text-center p-4 bg-background/50 backdrop-blur-xs rounded-xl border border-primary/20">
                      <div className="text-3xl font-bold text-primary mb-1">4x</div>
                      <p className="text-xs text-muted-foreground">Mehr Shares bei authentischem Content</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solutions Section */}
        <section id="solutions" ref={solutionsAnim.ref} style={solutionsAnim.style} className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Wir haben die <span className="text-gradient glow-text">Lösung</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Für jede Herausforderung bieten wir maßgeschneiderte Unterstützung
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Kamera-Coaching",
                  subtitle: "Von schüchtern zu selbstbewusst",
                  description: "Noch nie vor der Kamera gestanden? Kein Problem! Wir coachen Sie Schritt für Schritt, bis Sie sich wohlfühlen. Von der richtigen Haltung über Sprechtraining bis zum perfekten Blick in die Kamera – Sie werden überrascht sein, wie natürlich Sie wirken können.",
                  icon: Camera,
                  benefits: [
                    "Persönliches 1:1 Coaching",
                    "Entspannte Atmosphäre ohne Druck",
                    "Praktische Übungen & sofortiges Feedback",
                    "Tipps für authentische Präsenz"
                  ]
                },
                {
                  title: "Content-Strategie",
                  subtitle: "Nie wieder ideenlos",
                  description: "Keine Ideen mehr, was Sie posten sollen? Wir entwickeln einen durchdachten Content-Plan, der zu Ihrer Marke passt. Von Trend-Reels bis zu Behind-the-Scenes: Wir haben immer frische Ideen, die Ihre Zielgruppe lieben wird.",
                  icon: Lightbulb,
                  benefits: [
                    "3-Monats-Content-Kalender",
                    "Trend-Monitoring & Newsjacking",
                    "Branchen-spezifische Formate",
                    "Storytelling mit Strategie"
                  ]
                },
                {
                  title: "Full-Service-Produktion",
                  subtitle: "Wir übernehmen alles",
                  description: "Zu beschäftigt für Content Creation? Wir kommen zu Ihnen, drehen professionellen Content und kümmern uns um alles – von der Planung bis zum finalen Post. Sie investieren nur wenige Stunden, erhalten aber wochenlang hochwertigen Content.",
                  icon: Zap,
                  benefits: [
                    "Komplette Produktion & Editing",
                    "Minimaler Zeitaufwand für Sie",
                    "Wochen-Content in einem Drehtag",
                    "Regelmäßige Posting-Betreuung"
                  ]
                }
              ].map((solution, index) => (
                <Collapsible
                  key={index}
                  open={openSolutions.includes(index)}
                  onOpenChange={() => toggleSolution(index)}
                  className="relative group bg-gradient-to-br from-background to-primary/5 border border-primary/20 rounded-3xl p-8 hover:border-primary/40 transition-all duration-300 hover:scale-105"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="flex items-start justify-between gap-4 mb-6">
                      <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <solution.icon className="w-8 h-8 text-primary" />
                      </div>
                      <CollapsibleTrigger asChild>
                        <button className="p-2 rounded-lg hover:bg-primary/10 transition-colors">
                          <ChevronDown 
                            className={`w-6 h-6 text-primary transition-transform duration-300 ${
                              openSolutions.includes(index) ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      </CollapsibleTrigger>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{solution.title}</h3>
                    <p className="text-primary/80 font-medium mb-4">{solution.subtitle}</p>
                    
                    <AnimatedCollapsibleContent className="space-y-4">
                      <p className="text-muted-foreground leading-relaxed">{solution.description}</p>
                      <ul className="space-y-3">
                        {solution.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            </div>
                            <span className="text-sm">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </AnimatedCollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>

            {/* CTA after solutions */}
            <div className="text-center mt-12">
              <Button variant="hero" size="xl" onClick={() => setShowBooking(true)}>
                <Calendar className="w-5 h-5" />
                Jetzt Strategie-Gespräch buchen
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section id="services" ref={servicesAnim.ref} className={`section-padding transition-all duration-1000 ${servicesAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Unsere <span className="text-gradient glow-text">Leistungen</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Von der Strategie bis zur Umsetzung – alles aus einer Hand
              </p>
            </div>

            <SocialMediaServicesGrid />
          </div>
        </section>

        {/* Why Us Section */}
        <section id="why-us" ref={whyUsAnim.ref} className={`section-padding bg-gradient-to-b from-primary/5 to-background transition-all duration-1000 ${whyUsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Warum <span className="text-gradient glow-text">Clicks DMP?</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Wir sind mehr als nur eine Agentur – wir sind Ihr Partner für langfristigen Erfolg
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Users,
                  title: "Authentizität statt Perfektion",
                  description: "Wir glauben an echte Menschen, echte Geschichten. Keine gestellten Stock-Fotos, sondern authentischer Content, der Ihre Persönlichkeit zeigt und echte Verbindungen schafft."
                },
                {
                  icon: TrendingUp,
                  title: "Datengetriebene Kreativität",
                  description: "Schöner Content reicht nicht. Wir analysieren ständig Performance-Daten, lernen aus jedem Post und optimieren kontinuierlich für maximale Reichweite und Engagement."
                },
                {
                  icon: Sparkles,
                  title: "Trend-Expertise",
                  description: "Social Media ändert sich täglich. Wir sind immer am Puls der Zeit, kennen die neuesten Trends und wissen, wie Sie diese für Ihre Marke nutzen können – bevor Ihre Konkurrenz es tut."
                },
                {
                  icon: Camera,
                  title: "Professionelle Produktion",
                  description: "Von Cinema-Kameras bis zu professionellem Licht-Setup: Wir bringen Equipment im Wert von über 50.000€ mit – für Content, der sich von der Masse abhebt."
                },
                {
                  icon: Target,
                  title: "Ganzheitlicher Ansatz",
                  description: "Social Media funktioniert nicht isoliert. Wir denken Ihre gesamte Brand Story mit und sorgen dafür, dass alle Kanäle perfekt zusammenspielen."
                },
                {
                  icon: BarChart3,
                  title: "Transparente Erfolge",
                  description: "Sie bekommen regelmäßige Reports mit allen wichtigen Kennzahlen. So sehen Sie schwarz auf weiß, wie Ihre Social Media Präsenz wächst und welchen ROI Sie erzielen."
                }
              ].map((item, index) => (
                <Collapsible
                  key={index}
                  open={openWhyUs.includes(index)}
                  onOpenChange={() => toggleWhyUs(index)}
                  className="relative group bg-background border border-primary/20 rounded-2xl p-8 hover:border-primary/40 transition-all duration-300 hover:scale-105"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <item.icon className="w-12 h-12 text-primary group-hover:scale-110 transition-transform" />
                      <CollapsibleTrigger asChild>
                        <button className="p-2 rounded-lg hover:bg-primary/10 transition-colors">
                          <ChevronDown 
                            className={`w-6 h-6 text-primary transition-transform duration-300 ${
                              openWhyUs.includes(index) ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      </CollapsibleTrigger>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <AnimatedCollapsibleContent>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </AnimatedCollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Strategies */}
        <PlatformStrategies />

        {/* Content Calendar Example */}
        <ContentCalendarExample />

        {/* Success Metrics */}
        <SuccessMetrics />

        {/* Portfolio Section */}
        <section id="portfolio" ref={portfolioAnim.ref} className={`section-padding bg-gradient-to-b from-background to-primary/5 transition-all duration-1000 ${portfolioAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Unsere <span className="text-gradient glow-text">Social Media</span> Projekte
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Sehen Sie, wie wir Marken durch kreativen Social Media Content zum Leben erwecken
              </p>
              
              <MarketFilter 
                selectedMarket={selectedMarket}
                onMarketChange={setSelectedMarket}
                projectCount={socialMediaProjects.length}
              />
            </div>

            {projectsLoading ? (
              <div className="w-full max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <Skeleton className="aspect-[4/3] w-full" />
                      <div className="p-6 space-y-2">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : socialMediaProjects.length > 0 ? (
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  dragFree: false,
                  watchDrag: true,
                }}
                plugins={[
                  Autoplay({
                    delay: 2000,
                    stopOnInteraction: false,
                    stopOnMouseEnter: true,
                  })
                ]}
                className="w-full max-w-6xl mx-auto"
              >
                <CarouselContent className="-ml-4">
                  {socialMediaProjects.map((project) => (
                    <CarouselItem key={project.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                      <Link 
                        to={`/portfolio/${project.slug}`} 
                        className="group block"
                      >
                        <div className="relative overflow-hidden rounded-xl bg-card border border-border transition-all duration-500 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2">
                          {/* Image */}
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <LazyCoverImage
                              projectId={project.id}
                              category={project.category}
                              title={project.title}
                              className="w-full h-full"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                            
                            {/* Category Badge */}
                            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-xs border border-border">
                              <Share2 className="w-4 h-4 text-primary" />
                              <span className="text-xs capitalize">Social Media</span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
                              {project.title}
                            </h3>
                            <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                              {project.description}
                            </p>
                            
                            {project.client && (
                              <p className="text-xs text-muted-foreground mb-4">
                                Kunde: {project.client}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 text-primary text-sm font-medium">
                              <span>Projekt ansehen</span>
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex" />
                <CarouselNext className="hidden md:flex" />
              </Carousel>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  Wir arbeiten noch an unserer Website. Das Social Media Portfolio wird in Kürze verfügbar sein.
                </p>
              </div>
            )}

            {/* View All Portfolio Link */}
            {socialMediaProjects.length > 0 && (
              <div className="text-center mt-12">
                <Link to={`/portfolio?market=${selectedMarket}`}>
                  <Button variant="hero-outline" size="lg" className="group">
                    Alle Projekte ansehen
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Packages Section - Moved before FAQ */}
        <SocialMediaPackages />

        <GoogleRating />

        {/* FAQ Section */}
        <RegionalFAQSection
          title="Häufig gestellte Fragen zu Social Media Marketing"
          subtitle="Alles über Social Media Marketing in Mannheim, Heidelberg, Frankfurt und der Rhein-Neckar-Region"
          faqs={[
            {
              question: "Für welche Unternehmen in der Region arbeiten Sie?",
              answer: "Wir betreuen Unternehmen aller Größen in Mannheim, Heidelberg, Karlsruhe, Stuttgart, Frankfurt und der gesamten Rhein-Neckar-Region – von lokalen Restaurants und Retail-Stores bis zu großen Industrieunternehmen und Tech-Startups."
            },
            {
              question: "Was kostet professionelles Social Media Marketing?",
              answer: "Unsere Pakete starten ab 999€/Monat für 4 professionelle Videos. Je nach Umfang und Anforderungen passen wir die Pakete individuell an Ihre Bedürfnisse an. Kontaktieren Sie uns für ein maßgeschneidertes Angebot."
            },
            {
              question: "Welche Plattformen betreuen Sie?",
              answer: "Wir sind spezialisiert auf Instagram, TikTok, Facebook, LinkedIn und YouTube. Je nach Zielgruppe und Branche empfehlen wir die optimale Plattform-Strategie für Ihr Unternehmen."
            },
            {
              question: "Muss ich selbst vor die Kamera?",
              answer: "Nur wenn Sie möchten! Wir bieten Camera-Coaching für alle, die unsicher sind. Alternativ erstellen wir auch Content ohne Gesicht – Produkt-Showcases, B-Roll, Textbasierte Videos etc."
            },
            {
              question: "Wie schnell sehe ich Ergebnisse?",
              answer: "Erste Ergebnisse wie mehr Reichweite und Engagement sehen Sie oft schon nach 2-4 Wochen. Nachhaltiges Wachstum und messbare Business-Resultate entwickeln sich typischerweise nach 3-6 Monaten kontinuierlicher Arbeit."
            },
            {
              question: "Übernehmen Sie auch das Community Management?",
              answer: "Ja, auf Wunsch kümmern wir uns um Kommentare, Nachrichten und die Interaktion mit Ihrer Community. So bleiben Sie mit Ihren Followern in Kontakt, ohne selbst ständig online sein zu müssen."
            }
          ]}
        />

        <Footer />
        <BookingDialog open={showBooking} onOpenChange={setShowBooking} />
      </div>
    </>
  );
};

export default SocialMedia;
